import React, {useEffect, useMemo, useState} from "react"
import FormattedMessage, {useIntl} from "@irati/utils/lang"
import {useListRequestPoints} from "@irati/wallet/hooks/wallet"
import {ShowMoreButton, StatementItem as StatementItemComponent} from "../StatementItems/styled"
import {CurrencyType} from "@irati/payments/enums"
import {Row} from "@irati/ui/src/Layout"
import StatementItemTypes from "@irati/wallet/types/enums/StatementItemTypes"
import StatusType from "@irati/wallet/types/enums/Status"
import Flex from "@irati/ui/src/Flex"
import firstnameLastname from "@irati/identity/helpers/user/firstnameLastname"
import useAuth from "@irati/auth/helpers/auth/useAuth"
import {Body, H3} from "@irati/ui/src/Typography"
import {PointsRequestFilterType, PointsRequestStatus} from "@irati/types"
import Loading from "@irati/ui/src/Loading"
import Pill from "@irati/ui/src/Pill"
import {useTheme} from "styled-components"
import Button from "@irati/ui/src/Button"
import RequestPointsModal from "../RequestPointsModal"
import {Select} from "@irati/ui/src/FormItem"
import Tabs from "@irati/ui/src/Tabs"
const StatusMapping = {
  [PointsRequestStatus.StatusPending]: StatusType.POINTS_REQUEST_PENDING,
  [PointsRequestStatus.StatusApproved]: StatusType.POINTS_REQUEST_APPROVED,
  [PointsRequestStatus.StatusDeclined]: StatusType.POINTS_REQUEST_REJECTED,
  [PointsRequestStatus.StatusCancelled]: StatusType.POINTS_REQUEST_CANCELLED
}
const UpdatedDateText = {
  [PointsRequestStatus.StatusApproved]: "wallet.pointsRequests.statement.approved",
  [PointsRequestStatus.StatusDeclined]: "wallet.pointsRequests.statement.rejected",
  [PointsRequestStatus.StatusCancelled]: "wallet.pointsRequests.statement.cancelled"
}
const IdTypeOptions = [
  {
    label: "wallet.pointsRequests.filter.typeRequester",
    value: PointsRequestFilterType.TypeRequester
  },
  {
    label: "wallet.pointsRequests.filter.typeApprover",
    value: PointsRequestFilterType.TypeApprover
  }
]
const PointsRequests = () => {
  const theme = useTheme()
  const [isRequestPointsModalOpen, setIsRequestPointsModalOpen] = useState(false)
  const [idType, setIdType] = useState(PointsRequestFilterType.TypeRequester)
  const auth = useAuth()
  const canApprovePointsRequests = auth.productPermissions?.wallet?.canApprovePointsRequest
  const [pendingCount, setPendingCount] = useState(0)
  const intl = useIntl()
  const [status, setStatus] = useState(PointsRequestStatus.StatusPending)
  const {data, loading, refetch} = useListRequestPoints(
    {
      input: {
        status,
        idType,
        uuid: auth.user?.uuid
      }
    },
    {
      fetchPolicy: "no-cache"
    }
  )
  useEffect(() => {
    if (status !== PointsRequestStatus.StatusPending) return
    const toApprove = data?.results?.filter((item) => item.approver.uuid === auth.user?.uuid)
    setPendingCount(toApprove?.length || 0)
  }, [data, status])
  const items = useMemo(
    () =>
      data?.results?.map((item) => {
        return {
          statementItem: {
            uuid: item.uuid,
            transfer: {
              amount: item.amount,
              currency: CurrencyType.RP
            },
            content: {
              title: {
                text: intl.formatMessage(
                  {
                    id:
                      item.requester.uuid !== auth.user?.uuid
                        ? "wallet.pointsRequests.statement.requestedPointsOther"
                        : "wallet.pointsRequests.statement.requestedPoints"
                  },
                  {
                    requester: firstnameLastname(item.requester),
                    approver: firstnameLastname(item.approver)
                  }
                )
              },
              subTitle: {
                text: "platform.btn.viewDetails"
              },
              status: StatusMapping[item.status],
              type: StatementItemTypes.BE_SIMPLE_STATEMENT
            }
          },
          customDetails: (
            <Flex direction="column" gap={2}>
              <Flex gap={1}>
                <Body weight="bold" small>
                  <FormattedMessage id="wallet.pointsRequests.statement.submitted" />
                </Body>
                <Body small>{intl.formatDate(item.utcDateSubmitted * 1000)}</Body>
              </Flex>
              {item.status !== PointsRequestStatus.StatusPending && (
                <Flex gap={1}>
                  <Body weight="bold" small>
                    <FormattedMessage id={UpdatedDateText[item.status]} />
                  </Body>
                  <Body small>{intl.formatDate(item.utcDateUpdated * 1000)}</Body>
                </Flex>
              )}
              <Flex gap={1}>
                <Body weight="bold" small>
                  <FormattedMessage id="wallet.pointsRequests.statement.budget" />
                </Body>
                <Body small>{item.budget.name}</Body>
              </Flex>
              <Flex gap={1}>
                <Body weight="bold" small>
                  <FormattedMessage id="wallet.pointsRequests.statement.amount" />
                </Body>
                <Body small>{item.amount}</Body>
              </Flex>
              <Flex gap={1}>
                <Body weight="bold" small>
                  <FormattedMessage id="wallet.pointsRequests.statement.reason" />
                </Body>
                <Body small>{item.reason}</Body>
              </Flex>
              {item.responseMessage && (
                <Flex gap={1}>
                  <Body weight="bold" small>
                    <FormattedMessage id="wallet.pointsRequests.statement.response" />
                  </Body>
                  <Body small>{item.responseMessage}</Body>
                </Flex>
              )}
            </Flex>
          )
        }
      }),
    [data]
  )
  const handleNewPage = () => {
    refetch({
      input: {
        searchAfter: data?.cursor,
        status
      }
    })
  }
  const itemsList = useMemo(
    () => (
      <Flex direction="column" gap={2} marginTop={2}>
        {items?.map((item) => (
          <StatementItemComponent
            id={`points-request-item_${item.statementItem.uuid}`}
            statementItem={item.statementItem}
            customDetails={item.customDetails}
            key={item.statementItem.uuid}
          />
        ))}
        {items?.length === 0 && (
          <Flex justifyContent="center">
            <Button primary onClick={() => setIsRequestPointsModalOpen(true)}>
              <FormattedMessage id="wallet.pointsRequests.raiseRequest" />
            </Button>
          </Flex>
        )}
        {data?.cursor && (
          <ShowMoreButton onClick={handleNewPage}>
            <FormattedMessage id="wallet.statementItems.showMoreButton.text" values={{count: 10}} />
          </ShowMoreButton>
        )}
      </Flex>
    ),
    [items]
  )
  return (
    <>
      <Flex alignItems="center" justifyContent="space-between" wrap="wrap" marginBottom={2}>
        <H3>
          <FormattedMessage id="wallet.pointsRequests.title" />
        </H3>
        {canApprovePointsRequests && (
          <Row>
            <Select
              id="selectIdType"
              isClearable={false}
              isSearchable={false}
              value={idType}
              options={IdTypeOptions.map((option) => ({label: intl.formatMessage({id: option.label}), value: option.value}))}
              name="selectIdType"
              placeholder="wallet.pointsRequests.selectIdType"
              onChange={setIdType}
            />
          </Row>
        )}
      </Flex>
      <Tabs
        mountOnEnter
        onSelect={setStatus}
        tabs={[
          {
            key: PointsRequestStatus.StatusPending,
            title: (
              <Flex gap={1} alignItems="center">
                <FormattedMessage id="wallet.pointsRequests.pending.title" />
                {pendingCount > 0 && <Pill text={pendingCount} backgroundColor={theme.colors.tertiary.r_400} isWhiteText />}
              </Flex>
            ),
            component: loading ? <Loading useLoader={false} isLoading={loading} /> : itemsList
          },
          {
            key: PointsRequestStatus.StatusApproved,
            title: <FormattedMessage id="wallet.pointsRequests.approved.title" />,
            component: loading ? <Loading useLoader={false} isLoading={loading} /> : itemsList
          },
          {
            key: PointsRequestStatus.StatusDeclined,
            title: <FormattedMessage id="wallet.pointsRequests.rejected.title" />,
            component: loading ? <Loading useLoader={false} isLoading={loading} /> : itemsList
          }
        ]}
      />
      <RequestPointsModal show={isRequestPointsModalOpen} onHide={() => setIsRequestPointsModalOpen(false)} />
    </>
  )
}
export default PointsRequests
